import { useContext, useCallback } from 'preact/hooks';
import { batch } from '@preact/signals';
import { ChatContext } from './context.js';
import { useChat } from './useChat.js';
import {
  messages, isLoading, showTyping, showReview, showHistory,
  suggestions, clearMessages, resetForNewSession, showToast,
} from '../store/chatStore.js';
import { msgId } from '../utils/helpers.js';
import { MC } from '../utils/constants.js';

export function useSession() {
  const { cfg, api, session, bus, analytics } = useContext(ChatContext);
  const { addBot, addUser } = useChat();

  const makeWelcomeMsgs = useCallback(() => {
    const msgs = [];
    const ts   = Date.now();
    msgs.push({ id: msgId(), role: 'bot', text: cfg.welcomeMessage, timestamp: ts, animate: false });
    if (cfg.welcomeMessage2) {
      msgs.push({ id: msgId(), role: 'bot', text: cfg.welcomeMessage2, timestamp: ts + 1, animate: false });
    }
    return msgs;
  }, [cfg]);

  const loadHistory = useCallback(async () => {
    const sessionId = session.getOrCreate().sessionId;
    let msgs = session.getHistoryCache(sessionId);

    if (!msgs) {
      try {
        const data = await api.fetchHistory(sessionId);
        msgs = data.messages || [];
        session.setHistoryCache(sessionId, msgs);
      } catch {
        msgs = session.getHistory().map(m => ({
          role: m.role === 'bot' ? 'assistant' : m.role,
          content: m.text,
          timestamp: null,
        }));
      }
    }

    msgs.forEach(m => {
      const role = m.role === 'assistant' ? 'bot' : m.role;
      if (role === 'bot')  addBot(m.content, false, false, m.timestamp);
      if (role === 'user') addUser(m.content, m.timestamp);
    });
  }, [session, api, addBot, addUser]);

  const startNewChat = useCallback(async () => {
    const oldSessionId = session.getOrCreate().sessionId;

    try {
      await fetch(`${cfg.apiUrl}/chat/session/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: oldSessionId, app_id: cfg.appId, exit_url: location.href }),
      });
    } catch {}

    const newId = session._uid('s');
    session.clearHistory();
    session.clearHistoryCache(oldSessionId);
    session.setActiveSession(newId);
    const sessData = session.getOrCreate();
    api._sess = sessData;

    analytics?.track('/chat/session/create', {
      session_id:      newId,
      app_id:          cfg.appId,
      session_context: { entry_url: location.href, device_type: analytics?.deviceType, browser: analytics?.browser },
    }, 'newSession');

    resetForNewSession(makeWelcomeMsgs());
    bus.emit('session:new', { sessionId: newId });
  }, [cfg, session, api, bus, analytics, makeWelcomeMsgs]);

  const loadSession = useCallback((sessionRecord) => {
    session.setHistoryCache(sessionRecord.session_id, sessionRecord.messages);
    session.setActiveSession(sessionRecord.session_id);
    api._sess = session.getOrCreate();

    clearMessages();
    sessionRecord.messages.forEach(m => {
      const role = m.role === 'assistant' ? 'bot' : m.role;
      if (role === 'bot')  addBot(m.content, false, false, m.timestamp);
      if (role === 'user') addUser(m.content, m.timestamp);
    });
    bus.emit('session:load', { sessionId: sessionRecord.session_id });
  }, [session, api, bus, addBot, addUser]);

  return { loadHistory, startNewChat, loadSession, makeWelcomeMsgs };
}
